import type {
	AnimationsBehavior,
	BackgroundMedia,
	Media,
	Image,
	Video,
	FocalPoint,
	Overwrite,
} from '@wix/thunderbolt-becky-types'
import type { CompInfo } from '@wix/thunderbolt-components-native'

export const effectToIsFullScreenMap = {
	BackgroundReveal: true,
	BackgroundParallax: true,
	BackgroundZoom: true,
	BgParallax: true,
	BgReveal: true,
	BgZoomIn: true,
	BgFake3D: true,
}

const getMediaRef = (backgroundMedia: BackgroundMedia): Media | undefined =>
	// Use "unknown" type in order to support union types of mediaRef
	backgroundMedia.mediaRef

export const getMediaType = (backgroundMedia: BackgroundMedia): Media['type'] | undefined => {
	const mediaRef = getMediaRef(backgroundMedia)
	return mediaRef?.type
}

const isBgScrub = (viewMode: string) => (
	animation: AnimationsBehavior
): animation is Overwrite<AnimationsBehavior, { action: 'bgScrub'; name: keyof typeof effectToIsFullScreenMap }> =>
	animation.action === 'bgScrub' && (animation.viewMode || 'desktop').toLowerCase() === viewMode

export const getImageMediaRef = (backgroundMedia: BackgroundMedia): Image => getMediaRef(backgroundMedia) as Image

// TODO: clean up after type Video is updated in EE
export const getVideoMediaRef = (backgroundMedia: BackgroundMedia): Video & { focalPoint?: FocalPoint } =>
	getMediaRef(backgroundMedia) as Video & { focalPoint?: FocalPoint }

export const isVideoMedia = (
	backgroundMedia: BackgroundMedia
): backgroundMedia is BackgroundMedia & { mediaRef: Video } => getMediaType(backgroundMedia) === 'WixVideo'

export const isImageMedia = (
	backgroundMedia: BackgroundMedia
): backgroundMedia is BackgroundMedia & { mediaRef: Image } => getMediaType(backgroundMedia) === 'Image'

export const hasBackgroundScrubEffect = ({
	animations,
	viewMode,
}: Pick<CompInfo, 'viewMode'> & { animations: Array<AnimationsBehavior> }) => animations.find(isBgScrub(viewMode))

export const isMediaFullScreenByEffect = ({
	animations,
	viewMode,
}: Pick<CompInfo, 'viewMode'> & {
	animations: Array<AnimationsBehavior>
}): boolean | undefined => {
	const scrubAnimationName = getBgMediaEffectName({
		animations,
		viewMode,
	})
	if (scrubAnimationName) {
		return effectToIsFullScreenMap[scrubAnimationName]
	}
}

export const getBgMediaEffectName = ({
	animations,
	viewMode,
}: Pick<CompInfo, 'viewMode'> & {
	animations: Array<AnimationsBehavior>
}) => {
	const scrubAnimationName = animations.find(isBgScrub(viewMode))?.name

	return scrubAnimationName ? scrubAnimationName : undefined
}
